/**
 * Atualiza tags de branding PWA/iOS por-app. Complementa manifestLink.ts:
 * manifest cobre Chrome/Android "Add to Home Screen"; iOS Safari ignora
 * manifest e lê meta tags do HTML — sem isso, install no iPhone cai pro
 * `<title>Hook</title>` hardcoded e apple-touch-icon ausente.
 *
 * Também atualiza document.title + favicon pra que aba do browser mostre
 * o nome+ícone do app corrente (shell HTML é estático "Hook").
 *
 * Chamar com `null` em qualquer campo opcional só remove/ignora aquele
 * aspecto. Chamar `resetPwaMeta()` ao navegar fora de /app/:slug/ pra
 * voltar às defaults do shell (não fizemos isso pro manifest porque shell
 * não é PWA instalável por si só, mas title e favicon precisam voltar).
 */

const ICON_ID = 'pwa-app-icon';
const APPLE_ICON_ID = 'pwa-apple-touch-icon';
const THEME_COLOR_ID = 'pwa-theme-color';
const APPLE_TITLE_ID = 'pwa-apple-title';
const DEFAULT_TITLE = 'Hook';
const DEFAULT_ICON_HREF = '/hook-logo.svg';
const DEFAULT_THEME_COLOR = '#111';

export interface PwaMeta {
  name: string;
  logoUrl?: string | null;
  themeColor?: string | null;
}

function upsertLink(id: string, rel: string, href: string): void {
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = id;
    link.rel = rel;
    document.head.appendChild(link);
  }
  link.href = href;
}

function upsertMeta(id: string, name: string, content: string): void {
  let meta = document.getElementById(id) as HTMLMetaElement | null;
  if (!meta) {
    meta = document.createElement('meta');
    meta.id = id;
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function setDefaultFavicon(href: string): void {
  // Shell's static <link rel="icon"> (sem id) é o favicon default.
  // Trocamos o href dele em vez de criar outro — um só <link rel="icon">
  // no head pra evitar confusão de qual o browser usa.
  const link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (link) link.href = href;
}

export function updatePwaMeta(meta: PwaMeta): void {
  document.title = meta.name;

  upsertMeta(APPLE_TITLE_ID, 'apple-mobile-web-app-title', meta.name);

  if (meta.logoUrl) {
    setDefaultFavicon(meta.logoUrl);
    upsertLink(APPLE_ICON_ID, 'apple-touch-icon', meta.logoUrl);
  }

  if (meta.themeColor) {
    upsertMeta(THEME_COLOR_ID, 'theme-color', meta.themeColor);
  }
}

export function resetPwaMeta(): void {
  document.title = DEFAULT_TITLE;

  document.getElementById(APPLE_TITLE_ID)?.remove();
  document.getElementById(APPLE_ICON_ID)?.remove();
  document.getElementById(THEME_COLOR_ID)?.remove();

  setDefaultFavicon(DEFAULT_ICON_HREF);

  // Theme color do shell permanece no <meta name="theme-color" content="#111">
  // original do index.html — nosso #pwa-theme-color é SÓ o override. Ao remover,
  // o static volta a valer. Mas se algum browser ler o último <meta theme-color>
  // adicionado (spec ambígua), setamos explicitamente o default.
  const staticThemeMeta = document.querySelector(
    'meta[name="theme-color"]:not(#pwa-theme-color)',
  ) as HTMLMetaElement | null;
  if (staticThemeMeta) staticThemeMeta.content = DEFAULT_THEME_COLOR;
}
